import { useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { viewerInboxKeys } from '@/api/inbox';
import { useProfileDialogStore } from '@/store';
import { Box, Text } from '@/components/primitives';
import { List, ListItem, ListItemIcon, ListItemText } from '@/components/primitives';
import { Input } from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { reportMutationError } from '@/api/reportMutationError';
import { failureStatus } from '@/lib/nonInputFailureMessage';
import { verifyLockoutWords } from '@/lib/lockoutWords';
import {
  addContactMethod,
  removeContactMethod,
  verifyContactMethod,
} from '@/api';
import { Icons, Actions } from '@/theme';
import { ConfirmDialog } from '@/components/dialogs';
import { entities } from '@/constants';
import { testIds } from '@/test/testIds';
import { getValidationType } from '@/utils/contactMethodUtils';
import { parseMethodOwnedEnvelope } from '@/api/contactMethodClaim';
import { ContactMethodClaimDialog } from './ContactMethodClaimDialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const CONTACT_METHOD_ENTITY = entities.contactMethod;
const PENDING_METHODS_KEY = 'ving_pending_contact_methods';

interface PendingContactMethod {
  value: string;
  type: 'email' | 'phone';
  verificationCode: string;
}

function isValidPendingMethod(m: unknown): m is { value: string; type: 'email' | 'phone' } {
  if (typeof m !== 'object' || m === null) return false;
  const rec = m as Record<string, unknown>;
  if (typeof rec.value !== 'string' || rec.value.length === 0) return false;
  if (rec.type !== 'email' && rec.type !== 'phone') return false;
  return true;
}

function loadPendingMethods(userId: number): PendingContactMethod[] {
  try {
    const stored = localStorage.getItem(`${PENDING_METHODS_KEY}_${userId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(isValidPendingMethod)
        .map((m) => ({ value: m.value, type: m.type, verificationCode: '' }));
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

function savePendingMethods(userId: number, methods: PendingContactMethod[]): void {
  try {
    if (methods.length === 0) {
      localStorage.removeItem(`${PENDING_METHODS_KEY}_${userId}`);
    } else {
      const toStore = methods.map(({ value, type }) => ({ value, type, verificationCode: '' }));
      localStorage.setItem(`${PENDING_METHODS_KEY}_${userId}`, JSON.stringify(toStore));
    }
  } catch {
    // Ignore storage errors
  }
}

export interface ContactMethodsEditorProps {
  userId: number;
  emails: string[];
  phones: string[];
  /** Called after a contact method is added, verified, or removed. */
  onRefresh: () => Promise<void>;
  disabled?: boolean;
}

export function ContactMethodsEditor({
  userId,
  emails,
  phones,
  onRefresh,
  disabled = false,
}: ContactMethodsEditorProps) {
  const zone = useFeedbackZone();
  const queryClient = useQueryClient();
  const { isAdmin, isManager } = useCurrentUser();

  // A page asked for this dialog with the add input focused (#1400): focus it
  // once, then consume the request.
  const addInputRef = useRef<HTMLInputElement>(null);
  const addMethodRequested = useProfileDialogStore((s) => s.addMethodRequested);
  useEffect(() => {
    if (!addMethodRequested) return;
    // After the dialog's own open-focus has run.
    const t = window.setTimeout(() => {
      addInputRef.current?.focus();
      useProfileDialogStore.getState().clearAddMethodRequest();
    }, 0);
    return () => window.clearTimeout(t);
  }, [addMethodRequested]);

  // Input state — local classification only (H1: no manage-tier validator).
  const [methodInput, setMethodInput] = useState('');
  const trimmedInput = methodInput.trim();
  const validatedType = getValidationType(methodInput);
  const methodValid = validatedType !== null;
  // Error state: non-empty input that cannot be classified as email or phone.
  const methodInvalid = trimmedInput.length > 0 && !methodValid;

  // Pending (unverified) methods
  const [pendingMethods, setPendingMethods] = useState<PendingContactMethod[]>(() => {
    const verifiedMethods = new Set([...emails, ...phones]);
    return loadPendingMethods(userId).filter((m) => !verifiedMethods.has(m.value));
  });

  // Cross-user claim state (§7). Set when the add is refused because another
  // account has verified the method; carries vingapi's own opaque wording.
  const [claim, setClaim] = useState<{ method: string; refusalMessage: string } | null>(null);

  // Delete confirm state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);
  const [contactToDeleteType, setContactToDeleteType] = useState<'email' | 'phone'>('email');

  // Persist pending methods to localStorage
  useEffect(() => {
    savePendingMethods(userId, pendingMethods);
  }, [userId, pendingMethods]);

  // Sync pending methods when emails/phones change (e.g. after verification)
  useEffect(() => {
    const verified = new Set([...emails, ...phones]);
    setPendingMethods((prev) => prev.filter((m) => !verified.has(m.value)));
  }, [emails, phones]);

  // Mutations
  const addMutation = useApiMutation({
    mutationFn: addContactMethod,
    onSuccess: (_data, variables) => {
      const value = 'email' in variables ? variables.email! : variables.phone!;
      const type = 'email' in variables ? 'email' : 'phone';
      setPendingMethods((prev) => [...prev, { value, type, verificationCode: '' }]);
      setMethodInput('');
      zone?.push(`Verification code sent to ${value}`, 'success');
    },
    // Supplying onError REPLACES useApiMutation's default handling, so the
    // ordinary-failure branch is spelled out below rather than via
    // `errorMessage`.
    onError: (error, variables) => {
      // identity-plan.md §7: a method another account has VERIFIED isn't a
      // failed add, it's the start of a user merge — one contact method
      // identifies one person (D1). Offer the claim instead of an error.
      const owned = parseMethodOwnedEnvelope(error);
      if (owned) {
        const value = 'email' in variables ? variables.email! : variables.phone!;
        setClaim({ method: value, refusalMessage: owned.message });
        return;
      }
      reportMutationError(zone, error, 'Failed to add contact method.');
    },
  });

  const verifyMutation = useApiMutation({
    mutationFn: verifyContactMethod,
    onSuccess: async (_data, variables) => {
      const value = 'email' in variables ? variables.email! : variables.phone!;
      setPendingMethods((prev) => prev.filter((m) => m.value !== value));
      // useApiMutation awaits this onSuccess (#869), so the verify stays busy
      // until the list is re-read. A failed re-read is non-fatal: the write
      // succeeded and says so, and the list reloads with the next /me. It is
      // caught here, or TanStack would turn it into a failed verify.
      try {
        await onRefresh();
      } catch {
        // Non-fatal: see above.
      }
      // A newly verified method can resolve assignments sent to it (#1400,
      // Q-353): refetch every cached dashboard list now, not on the 60 s poll.
      await queryClient.invalidateQueries({ queryKey: viewerInboxKeys.all });
    },
    successMessage: 'Contact method verified.',
    // #1566 a: the verify lockout (423) says the approved words; every other failure keeps its line.
    onError: (error) => {
      reportMutationError(
        zone,
        error,
        failureStatus(error) === 423 ? { message: verifyLockoutWords(error) } : 'Verification failed.',
      );
    },
  });

  const resendMutation = useApiMutation({
    mutationFn: addContactMethod,
    onSuccess: (_data, variables) => {
      const value = 'email' in variables ? variables.email! : variables.phone!;
      zone?.push(`Verification code resent to ${value}`, 'success');
    },
    errorMessage: 'Failed to resend code.',
  });

  const removeMutation = useApiMutation({
    mutationFn: removeContactMethod,
    onSuccess: async () => {
      setContactToDelete(null);
      // Awaited (#869): the delete confirm stays loading until the list is
      // re-read. A failed re-read is non-fatal (the removal succeeded and says
      // so) and is caught here, or TanStack would turn it into a failed delete.
      try {
        await onRefresh();
      } catch {
        // Non-fatal: see above.
      }
    },
    successMessage: 'Contact method removed.',
    errorMessage: 'Failed to remove contact method.',
  });

  // Handlers
  const handleAdd = useCallback(() => {
    const trimmed = methodInput.trim();
    if (!methodValid || !validatedType) return;
    const data = validatedType === 'email' ? { email: trimmed } : { phone: trimmed };
    addMutation.mutate(data);
  }, [methodInput, methodValid, validatedType, addMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (methodValid) {
          handleAdd();
        }
      }
    },
    [methodValid, handleAdd]
  );

  const handlePendingCodeChange = useCallback((value: string, code: string) => {
    setPendingMethods((prev) =>
      prev.map((m) => (m.value === value ? { ...m, verificationCode: code } : m))
    );
  }, []);

  const handleVerify = useCallback(
    (method: PendingContactMethod) => {
      if (!method.verificationCode.trim()) return;
      const data =
        method.type === 'email'
          ? { email: method.value, verificationCode: method.verificationCode.trim() }
          : { phone: method.value, verificationCode: method.verificationCode.trim() };
      verifyMutation.mutate(data);
    },
    [verifyMutation]
  );

  const handleResend = useCallback(
    (method: PendingContactMethod) => {
      const data = method.type === 'email' ? { email: method.value } : { phone: method.value };
      resendMutation.mutate(data);
    },
    [resendMutation]
  );

  const handleCancelPending = useCallback((value: string) => {
    setPendingMethods((prev) => prev.filter((m) => m.value !== value));
  }, []);

  /**
   * D17 — an account must stay reachable. A user keeps at least one verified
   * method of any kind; a user holding a managing seat keeps at least one
   * verified EMAIL, because the surfaces those seats exist to use
   * (invitations, exports, security notices) are addressed by email.
   *
   * This deliberately does NOT try to reproduce vingapi's predicate exactly.
   * The server's condition is `is_premium_creator OR manages ANYWHERE`, and
   * neither is derivable here — /auth/me carries no premium flag and no
   * cross-context membership list, only the current context's level. So this
   * covers the case we can see (managing in the current context) and the
   * backend stays the authority: its refusal names the specific reason and
   * already surfaces through the mutation's error handling.
   */
  const managesHere = isAdmin || isManager;
  const canDeleteMethod = (type: 'email' | 'phone') => {
    if (emails.length + phones.length <= 1) return false;
    if (type === 'email' && managesHere && emails.length <= 1) return false;
    return true;
  };

  const handleDeleteClick = useCallback((contact: string, type: 'email' | 'phone') => {
    setContactToDelete(contact);
    setContactToDeleteType(type);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!contactToDelete) return;
    const data =
      contactToDeleteType === 'email'
        ? { email: contactToDelete }
        : { phone: contactToDelete };
    // Returning the promise hands the confirm its own lifecycle: it stays in a
    // loading state until the delete settles, then closes — whether that
    // settled as success or as failure. The refetch in onSuccess is part of
    // it: useApiMutation awaits an async onSuccess (#869). The outcome
    // surfaces on the profile dialog's band, because that's the surface this
    // editor belongs to. See ConfirmDialog's `onConfirm` docs.
    return removeMutation.mutateAsync(data);
  }, [contactToDelete, contactToDeleteType, removeMutation]);

  const allMethods = [
    ...emails.map((e) => ({ value: e, type: 'email' as const })),
    ...phones.map((p) => ({ value: p, type: 'phone' as const })),
  ];

  return (
    <>
      <Text variant="smallLabel" color="secondary" style={{ display: 'block', marginBottom: 1 }}>
        Contact Methods
      </Text>
      {(allMethods.length > 0 || pendingMethods.length > 0) && (
        <List dense disablePadding style={{ marginBottom: 8 }}>
          {allMethods.map((method, index) => (
            <ListItem key={method.value} disablePadding style={{ padding: '2px 0' }}>
              <ListItemIcon style={{ minWidth: 28 }}>
                {method.type === 'email' ? (
                  <Icons.email fontSize="small" color="success" />
                ) : (
                  <Icons.sms fontSize="small" color="success" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={method.value}
              />
              {canDeleteMethod(method.type) && (
                <ActionButton
                  action={Actions.delete}
                  mode="icon"
                  size="small"
                  onClick={() => handleDeleteClick(method.value, method.type)}
                  disabled={disabled || removeMutation.isPending}
                  testIdOverride={testIds.profile.deleteContactMethodButton(index)}
                />
              )}
            </ListItem>
          ))}

          {pendingMethods.map((method, index) => (
            <Box key={method.value} paddingTop="xs" paddingBottom="xs">
              <ListItem disablePadding style={{ padding: '2px 0' }}>
                <ListItemIcon style={{ minWidth: 28 }}>
                  {method.type === 'email' ? (
                    <Icons.email fontSize="small" color="warning" />
                  ) : (
                    <Icons.sms fontSize="small" color="warning" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={method.value}
                  secondary="Pending verification"
                  secondaryTypographyProps={{ style: { color: 'var(--color-warning-base)' } }}
                />
                <ActionButton
                  action={Actions.remove}
                  mode="icon"
                  size="small"
                  onClick={() => handleCancelPending(method.value)}
                  testIdOverride={testIds.profile.cancelPendingMethodButton(index)}
                />
              </ListItem>
              <Box display="flex" alignItems="center" gap="sm" marginTop="xs" style={{ marginLeft: '3.5' }}>
                <Input
                  placeholder="Enter code"
                  value={method.verificationCode}
                  onChange={(e) => handlePendingCodeChange(method.value, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && method.verificationCode.trim()) {
                      e.preventDefault();
                      handleVerify(method);
                    }
                  }}
                  disabled={verifyMutation.isPending}
                  inputProps={{ style: { fontSize: '0.875rem' } }}
                  style={{ flex: 1 }}
                />
                <ActionButton
                  action={Actions.verify}
                  mode="dialog-primary"
                  onClick={() => handleVerify(method)}
                  disabled={!method.verificationCode.trim()}
                  loading={verifyMutation.isPending}
                  label="Verify"
                  style={{ minWidth: 'auto', paddingLeft: 12, paddingRight: 12 }}
                />
                <ActionButton
                  action={Actions.resend}
                  mode="icon"
                  size="small"
                  onClick={() => handleResend(method)}
                  disabled={resendMutation.isPending}
                  loading={resendMutation.isPending}
                  tooltipOverride="Resend code"
                  testIdOverride={testIds.profile.resendCodeButton(index)}
                />
              </Box>
            </Box>
          ))}
        </List>
      )}

      {/* Add contact method input */}
      <Box display="flex" alignItems="center" gap="sm" marginBottom="lg">
        <Icons.add fontSize="small" color="action" />
        <Input
          placeholder="Add email or phone..."
          value={methodInput}
          onChange={(e) => setMethodInput(e.target.value)}
          onKeyDown={handleKeyDown}
          error={methodInvalid}
          disabled={disabled || addMutation.isPending}
          data-testid={testIds.profile.addContactMethodInput}
          inputRef={addInputRef}
          endAdornment={methodValid ? (
            <ActionButton
              action={Actions.add}
              mode="icon-compact"
              size="small"
              onClick={handleAdd}
              disabled={addMutation.isPending}
              testIdOverride={testIds.profile.addContactMethodButton}
            />
          ) : undefined}
          inputProps={{ style: { fontSize: '0.875rem' } }}
        />
      </Box>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={CONTACT_METHOD_ENTITY.labels.delete}
        message="Are you sure you want to delete this contact method?"
        confirmText="Yes"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteConfirmOpen(false)}
      />

      {claim && (
        <ContactMethodClaimDialog
          method={claim.method}
          refusalMessage={claim.refusalMessage}
          onCancel={() => setClaim(null)}
          onMerged={async () => {
            // The merge moved the claimed method onto this account, so the
            // list is stale — and the input still holds what they typed.
            setClaim(null);
            setMethodInput('');
            await onRefresh();
          }}
        />
      )}
    </>
  );
}
